import { useSpinDelay } from 'spin-delay'
import { Button, type ButtonVariant } from './button.tsx'
import { Icon } from './icon.tsx'
import { Spinner } from './spinner.tsx'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from './tooltip.tsx'

export const StatusButton = ({
	message,
	status,
	children,
	spinDelay,
	disabled,
	...props
}: React.ComponentProps<'button'> &
	ButtonVariant & {
		status: 'pending' | 'success' | 'error' | 'idle'
		message?: string | null
		spinDelay?: Parameters<typeof useSpinDelay>[1]
	}) => {
	const delayedPending = useSpinDelay(status === 'pending', {
		delay: 400,
		minDuration: 300,
		...spinDelay,
	})
	const companion = {
		pending: delayedPending ? (
			<Spinner size="sm" className="text-current" />
		) : null,
		success: (
			<div
				role="status"
				className="bg-brand-foreground/22 inline-flex size-4.5 items-center justify-center rounded-full"
			>
				<Icon name="check" className="size-3" title="success" />
			</div>
		),
		error: (
			<div
				role="status"
				className="bg-destructive text-destructive-foreground inline-flex size-4.5 items-center justify-center rounded-full"
			>
				<Icon name="cross-1" className="size-3" title="error" />
			</div>
		),
		idle: null,
	}[status]

	return (
		<Button {...props} disabled={disabled || status === 'pending'}>
			<div>{children}</div>
			{message ? (
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger>{companion}</TooltipTrigger>
						<TooltipContent>{message}</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			) : (
				companion
			)}
		</Button>
	)
}
StatusButton.displayName = 'Button'
